/**
 * Products list — estructura 1:1 con Skeleton actual:
 * - Header: título + dropdown actions + toggle grid/list + Add Product
 * - Filters: status pills (All/Active/Draft/Archived con counts) + search + category
 * - Grid responsive (2/3/4 cols) o List view (table)
 *
 * Look (mi mini-UI): mantener dark theme, redondeados, bordes neutros.
 */
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import { Button, Input, Label, Textarea, Badge, PageHeader, EmptyState, Card } from '@/components/ui'
import { ImageUploader } from '@/components/ImageUploader'
import { tenantApi } from '@/lib/api'
import { useAuth } from '@/lib/auth-store'
import { cn } from '@/lib/cn'
import {
  Search, Plus, Grid3x3, List, RefreshCw, Upload, X, Heart, Package,
  Edit, Eye, MoreVertical, DollarSign, Box, ChevronDown,
} from 'lucide-react'

export const Route = createFileRoute('/store/$id/products')({
  component: StoreProductsPage,
})

interface VariantDTO {
  id: string
  sku: string | null
  barcode: string | null
  status: 'draft' | 'published' | 'archived'
  attributes: Record<string, string>
  images: string[]
  prices: Array<{ currency: string; amount: number; compareAtAmount: number | null }>
  inventory: { quantity: number; reservedQuantity: number; lowStockThreshold: number } | null
}

interface ProductDTO {
  id: string
  slug: string
  brand: string | null
  sku: string | null
  status: 'draft' | 'published' | 'archived'
  images: string[]
  translations: Array<{ locale: string; name: string; description: string | null }>
  prices: Array<{ currency: string; amount: number; compareAtAmount: number | null }>
  reviewCount: number
  averageRating: number
  likeCount: number
  categories: Array<{ id: string; name: string }>
  variants: VariantDTO[]
  inventory: { quantity: number; reservedQuantity: number; lowStockThreshold: number } | null
  createdAt: string
}

function formatMoney(amount: number, currency: string): string {
  // amount viene en unidad mínima (centavos). Mostrar redondo: COP 200.000
  const value = amount / 100
  return `${currency} ${value.toLocaleString('es-CO', { maximumFractionDigits: 0 })}`
}

function variantDisplayName(v: VariantDTO): string {
  const attrs = Object.entries(v.attributes)
  if (attrs.length === 0) return v.sku ?? v.id.slice(0, 8)
  return attrs
    .map(([k, val]) => `${k.charAt(0).toUpperCase() + k.slice(1)} ${val}`)
    .join(' / ')
}

function totalStock(p: ProductDTO): number {
  if (p.variants.length > 0) {
    return p.variants.reduce((sum, v) => sum + (v.inventory?.quantity ?? 0), 0)
  }
  return p.inventory?.quantity ?? 0
}

function priceRange(p: ProductDTO): string {
  const base = p.prices[0]
  const variantPrices = p.variants.flatMap((v) => v.prices.map((vp) => vp.amount))
  const allPrices = variantPrices.length > 0 ? variantPrices : base ? [base.amount] : []
  if (allPrices.length === 0) return '—'
  const min = Math.min(...allPrices)
  const max = Math.max(...allPrices)
  const currency = base?.currency ?? p.variants[0]?.prices[0]?.currency ?? 'COP'
  if (min === max) return formatMoney(min, currency)
  return `${formatMoney(min, currency)} – ${formatMoney(max, currency)}`
}

type StatusFilter = 'all' | 'published' | 'draft' | 'archived'
type ViewMode = 'grid' | 'list'

function StoreProductsPage() {
  const { activeTenantId } = useAuth()
  const [showCreate, setShowCreate] = useState(false)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<ViewMode>('grid')

  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ['products', activeTenantId],
    queryFn: () => tenantApi.get<{ data: ProductDTO[] }>('/api/v1/products?limit=100'),
    enabled: !!activeTenantId,
  })

  const allProducts = data?.data ?? []

  const filtered = useMemo(() => {
    let list = allProducts
    if (statusFilter !== 'all') list = list.filter((p) => p.status === statusFilter)
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      list = list.filter((p) => {
        const name = p.translations[0]?.name?.toLowerCase() ?? ''
        return name.includes(q) || p.slug.toLowerCase().includes(q) || (p.sku?.toLowerCase().includes(q) ?? false)
      })
    }
    return list
  }, [allProducts, statusFilter, searchQuery])

  const statusCounts = useMemo(() => ({
    all: allProducts.length,
    published: allProducts.filter((p) => p.status === 'published').length,
    draft: allProducts.filter((p) => p.status === 'draft').length,
    archived: allProducts.filter((p) => p.status === 'archived').length,
  }), [allProducts])

  return (
    <>
      <PageHeader
        title="Productos"
        description={`${filtered.length} de ${allProducts.length} producto(s)`}
        action={
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isRefetching} title="Recargar">
              <RefreshCw className={`size-4 ${isRefetching ? 'animate-spin' : ''}`} />
            </Button>
            <Button variant="outline" size="sm" disabled title="Importar CSV (próxima iteración)">
              <Upload className="size-4 mr-2" /> Importar
            </Button>
            <div className="flex items-center border border-border rounded-md p-0.5">
              <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded transition ${viewMode === 'grid' ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]' : 'text-muted-foreground hover:text-foreground'}`} title="Vista grid">
                <Grid3x3 className="size-4" />
              </button>
              <button onClick={() => setViewMode('list')} className={`p-1.5 rounded transition ${viewMode === 'list' ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]' : 'text-muted-foreground hover:text-foreground'}`} title="Vista lista">
                <List className="size-4" />
              </button>
            </div>
            <Button onClick={() => setShowCreate(true)}>
              <Plus className="size-4 mr-2" /> Nuevo producto
            </Button>
          </div>
        }
      />

      <div className="p-8 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            {(['all', 'published', 'draft', 'archived'] as const).map((status) => {
              const label = status === 'all' ? 'Todos' : status === 'published' ? 'Publicados' : status === 'draft' ? 'Borrador' : 'Archivados'
              const active = statusFilter === status
              return (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition ${
                    active ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]' : 'bg-[hsl(var(--surface))] text-foreground border border-border hover:border-foreground/30'
                  }`}
                >
                  {label}
                  <span className={`ml-1.5 text-xs ${active ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
                    ({statusCounts[status]})
                  </span>
                </button>
              )
            })}
          </div>

          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input type="search" placeholder="Buscar por nombre, slug o SKU..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
          </div>

          <Button variant="outline" size="sm" disabled className="h-9" title="Filtrar por categoría (próxima iteración)">
            Categoría <ChevronDown className="size-4 ml-2" />
          </Button>
        </div>

        {isLoading && <p className="text-sm text-muted-foreground">Cargando productos...</p>}
        {error && (
          <Card className="p-4 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
            <p className="text-sm text-red-700 dark:text-red-400">{(error as Error).message}</p>
          </Card>
        )}
        {!isLoading && filtered.length === 0 && allProducts.length > 0 && (
          <EmptyState title="Sin resultados" description="Ningún producto coincide con los filtros." />
        )}
        {!isLoading && allProducts.length === 0 && (
          <EmptyState title="Sin productos" description="Crea el primer producto con el botón de arriba." />
        )}

        {filtered.length > 0 && viewMode === 'grid' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
        {filtered.length > 0 && viewMode === 'list' && (
          <ProductsTable products={filtered} />
        )}
      </div>

      {showCreate && <CreateProductModal onClose={() => setShowCreate(false)} />}
    </>
  )
}

/**
 * ProductCard — réplica visual de Skeleton actual:
 *   - Header horizontal: imagen 80x80 izquierda + info (nombre, SKU, tags categoría)
 *   - Row precio + total uds (badge color según stock)
 *   - Lista de variantes: nombre + precio + stock (bolita verde/amarilla/roja)
 *   - "+N variantes más" si hay más de 3
 *   - Footer: actions (Edit, Eye, More) izquierda + status badge derecha
 */
function ProductCard({ product }: { product: ProductDTO }) {
  const name = product.translations[0]?.name ?? product.slug
  const stock = totalStock(product)
  const stockBadgeClass =
    stock === 0 ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800' :
    stock < 20 ? 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800' :
                 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
  const stockLabel = stock === 0 ? 'Sin stock' : `${stock} uds`

  const statusVariant = product.status === 'published' ? 'success' : product.status === 'draft' ? 'warning' : 'muted'
  const statusLabel = product.status === 'published' ? 'Activo' : product.status === 'draft' ? 'Borrador' : 'Archivado'

  const visibleVariants = product.variants.slice(0, 3)
  const hiddenCount = product.variants.length - visibleVariants.length

  return (
    <Card className="p-4 group hover:border-border transition">
      {/* Header: image + info */}
      <div className="flex gap-3 mb-3">
        <div className="size-20 flex-shrink-0 rounded-md border border-border overflow-hidden bg-background">
          {product.images[0] ? (
            <img src={product.images[0]} alt={name} className="size-full object-cover" />
          ) : (
            <div className="size-full flex items-center justify-center text-muted-foreground">
              <Package className="size-8" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm text-foreground line-clamp-2 leading-tight">{name}</h3>
          <p className="text-xs text-muted-foreground mt-1 font-mono uppercase truncate">{product.sku ?? product.slug}</p>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            {product.categories.slice(0, 2).map((c) => (
              <span key={c.id} className="px-2 py-0.5 text-[10px] bg-[hsl(var(--muted))] text-foreground rounded-md">
                {c.name}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Price + Stock row */}
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <DollarSign className="size-3.5 text-muted-foreground shrink-0" />
          <span className="text-sm font-semibold text-foreground truncate font-mono">{priceRange(product)}</span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Box className="size-3.5 text-muted-foreground" />
          <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full border', stockBadgeClass)}>
            {stockLabel}
          </span>
        </div>
      </div>

      {/* Variantes desplegadas */}
      {product.variants.length > 0 ? (
        <div className="space-y-1 mb-3 bg-background/50 border border-border rounded-md p-2">
          {visibleVariants.map((v) => {
            const qty = v.inventory?.quantity ?? 0
            const dotColor = qty === 0 ? 'bg-red-500' : qty < 20 ? 'bg-yellow-500' : 'bg-emerald-500'
            const vPrice = v.prices[0]
            return (
              <div key={v.id} className="flex items-center text-xs text-foreground">
                <span className="flex-1 truncate text-foreground">{variantDisplayName(v)}</span>
                <span className="font-mono text-muted-foreground mx-2">
                  {vPrice ? formatMoney(vPrice.amount, vPrice.currency) : '—'}
                </span>
                <div className="flex items-center gap-1 shrink-0">
                  <div className={cn('size-2 rounded-full', dotColor)} />
                  <span className="text-muted-foreground tabular-nums">({qty})</span>
                </div>
              </div>
            )
          })}
          {hiddenCount > 0 && (
            <p className="text-xs text-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] font-medium cursor-pointer">
              +{hiddenCount} variantes más
            </p>
          )}
        </div>
      ) : (
        <div className="mb-3 text-xs text-muted-foreground bg-background/50 border border-border rounded-md p-2 text-center">
          Sin variantes
        </div>
      )}

      {/* Footer: actions + status */}
      <div className="flex items-center justify-between pt-3 border-t border-border">
        <div className="flex items-center gap-1">
          <button className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))] transition" title="Editar">
            <Edit className="size-4" />
          </button>
          <button className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))] transition" title="Ver en tienda">
            <Eye className="size-4" />
          </button>
          <button className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))] transition" title="Más opciones">
            <MoreVertical className="size-4" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          {product.likeCount > 0 && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Heart className="size-3 text-red-700 dark:text-red-400" />
              {product.likeCount}
            </span>
          )}
          <Badge variant={statusVariant}>{statusLabel}</Badge>
        </div>
      </div>
    </Card>
  )
}

function ProductsTable({ products }: { products: ProductDTO[] }) {
  return (
    <Card>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-background border-b border-border text-xs text-muted-foreground uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Producto</th>
              <th className="text-left px-4 py-3">SKU</th>
              <th className="text-left px-4 py-3">Estado</th>
              <th className="text-right px-4 py-3">Precio</th>
              <th className="text-center px-4 py-3">Stock</th>
              <th className="text-right px-4 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {products.map((p) => {
              const name = p.translations[0]?.name ?? p.slug
              const stock = totalStock(p)
              const stockColor = stock === 0 ? 'text-red-700 dark:text-red-400' : stock < 20 ? 'text-yellow-700 dark:text-yellow-400' : 'text-emerald-700 dark:text-emerald-400'
              const statusVariant = p.status === 'published' ? 'success' : p.status === 'draft' ? 'warning' : 'muted'
              const statusLabel = p.status === 'published' ? 'Activo' : p.status === 'draft' ? 'Borrador' : 'Archivado'
              return (
                <tr key={p.id} className="hover:bg-[hsl(var(--surface))]">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {p.images[0] ? (
                        <img src={p.images[0]} alt={name} className="size-10 rounded object-cover" />
                      ) : (
                        <div className="size-10 rounded bg-[hsl(var(--muted))] flex items-center justify-center">
                          <Package className="size-4 text-muted-foreground" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-foreground">{name}</p>
                        <p className="text-xs text-muted-foreground">{p.variants.length} variantes</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{p.sku ?? '—'}</td>
                  <td className="px-4 py-3"><Badge variant={statusVariant}>{statusLabel}</Badge></td>
                  <td className="px-4 py-3 text-right font-mono text-foreground">{priceRange(p)}</td>
                  <td className={cn('px-4 py-3 text-center font-medium tabular-nums', stockColor)}>
                    {stock === 0 ? 'Sin stock' : `${stock}`}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))]" title="Editar">
                        <Edit className="size-4" />
                      </button>
                      <button className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))]" title="Ver">
                        <Eye className="size-4" />
                      </button>
                      <button className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))]" title="Más">
                        <MoreVertical className="size-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

function CreateProductModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const { activeTenantId } = useAuth()
  const [slug, setSlug] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [priceCop, setPriceCop] = useState('')
  const [images, setImages] = useState<string[]>([])
  const [sku, setSku] = useState('')
  const [brand, setBrand] = useState('')
  const [status, setStatus] = useState<'draft' | 'published'>('published')

  const create = useMutation({
    mutationFn: (input: unknown) => tenantApi.post('/api/v1/products', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['products', activeTenantId] })
      onClose()
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    create.mutate({
      slug,
      sku: sku || null,
      brand: brand || null,
      status,
      isActive: true,
      images,
      taxRate: 19,
      translations: [{ locale: 'es', name, description }, { locale: 'en', name, description }],
      prices: [{ currency: 'COP', amount: Math.round(parseFloat(priceCop || '0') * 100) }],
      categoryIds: [],
    })
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-lg font-semibold">Nuevo producto</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="size-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div><Label>Slug (URL) *</Label><Input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} required placeholder="nuevo-producto" /></div>
          <div><Label>Nombre *</Label><Input value={name} onChange={(e) => setName(e.target.value)} required /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>SKU</Label><Input value={sku} onChange={(e) => setSku(e.target.value)} placeholder="EUT-05-M" /></div>
            <div><Label>Marca</Label><Input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Skeleton" /></div>
          </div>
          <div><Label>Descripción</Label><Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Precio (COP) *</Label><Input type="number" value={priceCop} onChange={(e) => setPriceCop(e.target.value)} required placeholder="149000" /></div>
            <div>
              <Label>Status</Label>
              <select value={status} onChange={(e) => setStatus(e.target.value as 'draft' | 'published')} className="w-full h-9 px-3 bg-[hsl(var(--surface))] border border-border rounded-md text-sm">
                <option value="published">Publicado</option>
                <option value="draft">Borrador</option>
              </select>
            </div>
          </div>
          <div>
            <Label>Imágenes</Label>
            <ImageUploader value={images} onChange={setImages} folder="products" maxImages={10} />
          </div>
          {create.error && <div className="p-3 rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-400">{(create.error as Error).message}</div>}
          <div className="flex gap-2 justify-end pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={create.isPending}>{create.isPending ? 'Creando...' : 'Crear producto'}</Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
